import { TMember } from "types/user.types";
import { IReservationModalMethods } from "./types";
import { TAttendeeLimit, TMeetingRoom } from "apis/meetings/meeting-room";
import {
  checkDuplicationRecurringReservation,
  createMeetingReservation,
  TFormattedAttendee,
} from "apis/meetings/meeting-reservation";
import { min } from "lodash";

export const services: IReservationModalMethods = {
  checkChanges: (
    initData: any,
    title: string,
    isEditedAttendees: boolean,
    isPrivate: boolean,
    guestsCanModify: boolean,
    isSelectedMeetingRoom: boolean,
    memo?: string
  ) => {
    if (title !== initData.title) return true;
    if (isEditedAttendees) return true;
    if (isSelectedMeetingRoom) return true;
    if (memo !== undefined && memo.length > 0) return true;
    if (isPrivate !== initData.isPrivate) return true;
    if (guestsCanModify !== initData.guestsCanModify) return true;

    return false;
  },

  getInitStartTime: (selectedDate: Date, selectedStartTime?: Date) => {
    const today = new Date();
    const hour = selectedStartTime
      ? selectedStartTime.getHours()
      : today.getMinutes() > 30
        ? today.getHours() + 1
        : today.getHours();
    let minutes = selectedStartTime ? selectedStartTime.getMinutes() : today.getMinutes();
    minutes = minutes === 0 ? 0 : minutes < 30 ? 0 : 30;

    const newDate = new Date(selectedDate);
    newDate.setHours(hour, minutes, 0, 0);
    return newDate;
  },

  getInitEndTime: (selectedDate: Date, selectedStartTime?: Date) => {
    const after30Minutes = selectedStartTime ?? new Date();
    after30Minutes.setMinutes(after30Minutes.getMinutes() + 30);

    const hour = after30Minutes.getHours() > 30 ? after30Minutes?.getHours() + 1 : after30Minutes?.getHours();
    const minutes = after30Minutes.getMinutes() === 0 ? 0 : after30Minutes.getMinutes() < 30 ? 30 : 0;

    const newDate = new Date(selectedDate);
    newDate.setHours(hour, minutes, 0, 0);
    return newDate;
  },

  getInitInfo: (memberInfo: TMember, defaultDate: Date) => {
    const title = `${memberInfo.nickname}의 회의실 예약`;
    const attendees = [
      {
        isVisitor: false,
        id: memberInfo.id,
        email: memberInfo.email ?? "",
        picture: memberInfo.image,
        name: memberInfo.nickname,
      },
    ];
    const date = defaultDate < new Date() ? new Date() : defaultDate;
    const memo = undefined;
    const isPrivate = false;
    const guestsCanModify = false;

    return { title, attendees, date, memo, isPrivate, guestsCanModify };
  },

  reservation: async (
    defaultDate: Date,
    selectedStartTime: Date,
    selectedEndTime: Date,
    title: string,
    isPrivate: boolean,
    guestsCanModify: boolean,
    selectedMeetingRoom: TMeetingRoom,
    attendees: TFormattedAttendee[],
    memo?: string,
    rrule?: string,
    exceptedAttendeeLimit?: boolean
  ) => {
    const startRsvTime = new Date(defaultDate);
    if (selectedStartTime) startRsvTime.setHours(selectedStartTime?.getHours(), selectedStartTime.getMinutes(), 0, 0);

    const endRsvTime = new Date(defaultDate);
    if (selectedEndTime) endRsvTime.setHours(selectedEndTime.getHours(), selectedEndTime.getMinutes(), 0, 0);

    const data = {
      title: title ?? "",
      memo: memo ?? "",
      isPrivate: isPrivate,
      guestsCanModify: guestsCanModify,
      rsvStartTime: startRsvTime.toISOString(),
      rsvEndTime: endRsvTime.toISOString(),
      meetingRoomId: selectedMeetingRoom?.id ?? "",
      reservationAttendees: attendees.map((attendee) => {
        return {
          isVisitor: attendee.isVisitor,
          visitorEmail: attendee.email,
          memberId: attendee.id,
        };
      }),
      rrule: rrule,
      exceptedAttendeeLimit: exceptedAttendeeLimit,
    };

    const res = await createMeetingReservation(data);
    return res;
  },

  //* INFO: 반복 예약 중복 체크
  checkRecurringReservationDuplicate: async (
    selectedStartTime: Date,
    selectedEndTime: Date,
    selectedMeetingRoom: TMeetingRoom,
    rrule: string,
    defaultDate: Date
  ) => {
    const startRsvTime = new Date(defaultDate);
    if (selectedStartTime) startRsvTime.setHours(selectedStartTime?.getHours(), selectedStartTime.getMinutes(), 0, 0);

    const endRsvTime = new Date(defaultDate);
    if (selectedEndTime) endRsvTime.setHours(selectedEndTime.getHours(), selectedEndTime.getMinutes(), 0, 0);

    const res = await checkDuplicationRecurringReservation({
      rrule: rrule,
      meetingRoomId: selectedMeetingRoom.id,
      rsvStartTime: startRsvTime.toISOString(),
      rsvEndTime: endRsvTime.toISOString(),
    });

    if (res.success && res.data) {
      return res.data;
    } else return null;
  },

  //* INFO: 현재 시간에 포함된 참석자 예약 제한 수
  getAttendeeLimit: (attendeeLimits: TAttendeeLimit[], startTime: Date, endTime: Date) => {
    const startTimeStr = startTime.toTimeString().split(" ")[0];
    const endTimeStr = endTime.toTimeString().split(" ")[0];

    // 주어진 시간 범위에 포함되는 attendeeLimits 필터링
    const includedLimits = attendeeLimits.filter((limit) => {
      return limit.startTime < endTimeStr && limit.endTime > startTimeStr;
    });

    // 포함된 시간이 없으면 기본값 0 반환
    if (includedLimits.length === 0) {
      return 0;
    }

    // 포함된 시간 중 최대 minAttendeeNum 찾기
    return Math.max(...includedLimits.map((limit) => limit.minAttendeeNum));
  },
};
